import { getTableQuery } from "@/context/Table";
import useAuth from "@/hooks/useAuth";
import { paginateTable } from "@/mocks/utils/helpers";
import { TAssetItemForm } from "@/modules/AssetItemForm";
import { TAssetTypeForm } from "@/modules/AssetTypeForm";
import { TCategoryForm } from "@/modules/CategoryForm";
import { TManufacturerForm } from "@/modules/ManufacturerForm";
import { TModelForm } from "@/modules/ModelForm";
import { TSupplierForm } from "@/modules/SupplierForm";
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  RawAxiosRequestHeaders,
} from "axios";
import { components } from "../../swagger";
import { TTagForm } from "@/modules/TagForm";
import * as Sentry from "@sentry/react";
import { TStocksForm } from "@/modules/StockForm";
const API_URL = import.meta.env.VITE_APP_API_URL;

/**
 * Handles the auth bearer token captured when user is logged in and pass to axios instance.
 *
 * Token is captured from the useAuth hook.
 * @param axios
 *
 */
export function setupAPI(axios: any) {
  axios.defaults.headers.Accept = "application/json";
  axios.interceptors.request.use(
    (config: { headers: { Authorization: string } }) => {
      const auth = useAuth.getState().auth;
      if (auth && auth.token) {
        config.headers.Authorization = `Bearer ${auth.token}`;
      }

      return config;
    },
    (err: any) => Promise.reject(err)
  );
  axios.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      // console.log(error);
      if (error.response) {
        if (error.response.status === 401) {
          const logout = useAuth.getState().logout;
          logout();
          // Skip 401 errors
          return Promise.resolve(error);
        }

        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        Sentry.captureException(error.response.data);
      } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        console.log(error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        Sentry.captureException(error.message);
      }
      Sentry.captureException(error);
      return Promise.reject(error);
    }
  );
}

// API PATHS
const USER_URL = `${API_URL}/users`;
const ROLE_URL = `${API_URL}/role`;
const TRANSACTION_TYPE_URL = `${API_URL}/transactiontypes`;
const LOCATION_URL = `${API_URL}/locations`;
const CATEGORY_URL = `${API_URL}/assetgroups`;
const ASSET_TYPE_URL = `${API_URL}/assettypes`;
const ASSET_ITEM_URL = `${API_URL}/assetitems`;
const SUPPLIER_URL = `${API_URL}/suppliers`;
const COUNTRY_URL = `${API_URL}/countries`;
const MANUFACTURER_URL = `${API_URL}/manufacturers`;
const MODEL_URL = `${API_URL}/models`;
const DASHBOARD_URL = `${API_URL}/dashboards`;
const ASSETISSUE_URL = `${API_URL}/assetissue`;
const TRANSACTIONS_URL = `${API_URL}/transactions`;
const TRANSACTIONOPERATIONS_URL = `${API_URL}/transactionoperations`;
const TRANSACTIONGROUPS_URL = `${API_URL}/transactiongroups`;
const TRANSACTION_GROUP_ITEM_URL = `${API_URL}/transactiongroupitems`;
const REPORT_URL = `${API_URL}/reports`;
const STOCKOUT_URL = `${API_URL}/stockouts`;
const STOCKTAKELINEITEMS_URL = `${API_URL}/stocktakelineitems`;
const STOCKTAKEEVENTS_URL = `${API_URL}/stocktakeevents`;
const STOCKTAKES_URL = `${API_URL}/stocktakes`;
const TAG_URL = `${API_URL}/tags`;
const PRINTER_URL = `${import.meta.env.VITE_APP_PRINTER_URL}`;
const ASSETOPERATION_URL = `${API_URL}/assetoperations`;
const BOAT_TYPE_URL = `${API_URL}/boattypes`;
const LOGS_URL = `${API_URL}/logs`;
const PERMISSIONSET_URL = `${API_URL}/permissionsets`;
const EMAIL_ALERT_GROUP_URL = `${API_URL}/notificationgroups`;
const NOTIFICATION_URL = `${API_URL}/notifications`;
const GANTRY_URL = `${API_URL}/gantries`;
const INCOMING_TYPE_URL = `${API_URL}/incomingtypes`;
const STOCKS_URL = `${API_URL}/stocks`;
const STOCK_ITEM_URL = `${API_URL}/stockitem`;
const TRANSACTION_GROUP_URL = `${API_URL}/transactiongroups`;
const ASSET_ITEM_LOCATION_URL = `${API_URL}/assetitemlocations`;
const ASSET_TYPE_LOCATION_URL = `${API_URL}/assettypelocations`;
const MEASUREMENT_UNIT_URL = `${API_URL}/unitofmeasurements`;
const ALLOCATION_URL = `${API_URL}/allocations`;
const TRANSACTION_GROUP_STATUS_URL = `${API_URL}/transactiongroupstatuses`;
//#region TransactionType API
export const getTransactionTypeById = async (
  id: string
): Promise<DefaultResponseWithData<TTransactionType>> => {
  return axios
    .get(`${TRANSACTION_TYPE_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TTransactionType>>) =>
        response.data
    );
};

const getTransactionTypes = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TTransactionTypeList>> => {
  return axios
    .post(`${TRANSACTION_TYPE_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TTransactionTypeList>>) =>
        response.data
    );
};

//#endregion TransactionType API

//#region Authentication API
export const getAuthToken = async (payload: {
  username: string;
  password: string;
}): Promise<DefaultPayloadResponse<AuthToken>> => {
  return axios
    .post(`${API_URL}/token`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<AuthToken>>) =>
        response.data
    );
};

//#endregion Athentication API

//#region Category API
const createCategory = async (
  payload: TCategoryForm
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${CATEGORY_URL}`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};

const getCategory = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TCategory>> => {
  return axios
    .post(`${CATEGORY_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TCategory>>) =>
        response.data
    );
};
export const getCategoryById = async (
  id: string
): Promise<DefaultResponseWithData<TCategory>> => {
  return axios
    .get(`${CATEGORY_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TCategory>>) =>
        response.data
    );
};
const editCategory = async (
  id: string,
  payload: TCategoryForm
): Promise<DefaultResponse> => {
  return axios
    .put(`${CATEGORY_URL}/${id}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const deleteCategory = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${CATEGORY_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};
//#endregion Category API

//#region Asset Type API
const getAssetType = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TAssetType>> => {
  return axios
    .post(`${ASSET_TYPE_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TAssetType>>) =>
        response.data
    );
};
const createAssetType = async (
  payload: TAssetTypeForm
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${ASSET_TYPE_URL}`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};
export const getAssetTypeById = async (
  id: string
): Promise<DefaultResponseWithData<TAssetType>> => {
  return axios
    .get(`${ASSET_TYPE_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TAssetType>>) =>
        response.data
    );
};
const editAssetType = async (
  id: string,
  payload: TAssetTypeForm
): Promise<DefaultResponse> => {
  return axios
    .put(`${ASSET_TYPE_URL}/${id}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const deleteAssetType = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${ASSET_TYPE_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};
//#endregion Asset Type API

//#region Asset Item API
const getAssetItem = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TAssetItem>> => {
  return axios
    .post(`${ASSET_ITEM_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TAssetItem>>) =>
        response.data
    );
};

const createAssetItem = async (
  payload: TAssetItemForm
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${ASSET_ITEM_URL}`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};

const createMultipleAssetItem = async (
  payload: TAssetItemBulkRequest
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${ASSET_ITEM_URL}/bulkcreate`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};

export const getAssetItemById = async (
  id: string
): Promise<DefaultResponseWithData<TAssetItem>> => {
  return axios
    .get(`${ASSET_ITEM_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TAssetItem>>) =>
        response.data
    );
};
const editAssetItem = async (
  id: string,
  payload: TAssetItemForm
): Promise<DefaultResponse> => {
  return axios
    .put(`${ASSET_ITEM_URL}/${id}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const deleteAssetItem = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${ASSET_ITEM_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const updateBatchNumber = async (
  id: string,
  payload: TUpdateBatchNumberRequest
): Promise<DefaultResponse> => {
  return axios
    .put(`${ASSET_ITEM_URL}/updateBatchNumber/${id}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};
//#endregion Asset Item API

//#region Locations API
const getLocations = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TLocation>> => {
  return axios
    .post(`${LOCATION_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TLocation>>) =>
        response.data
    );
};

const getWarehouses = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TLocation>> => {
  payload.filters = payload.filters
    ? `locationType@=*WAREHOUSE,${payload.filters}`
    : "locationType@=*WAREHOUSE";
  return axios
    .post(`${LOCATION_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TLocation>>) =>
        response.data
    );
};
const createLocation = async (
  payload: TCategoryForm
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${LOCATION_URL}`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};
export const getLocationById = async (
  id: string
): Promise<DefaultResponseWithData<TLocation>> => {
  return axios
    .get(`${LOCATION_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TLocation>>) =>
        response.data
    );
};
const editLocation = async (
  id: string,
  payload: TCategoryForm
): Promise<DefaultResponse> => {
  return axios
    .put(`${LOCATION_URL}/${id}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};
const deleteLocation = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${LOCATION_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

//#endregion Locations API

//#region Supplier API
const getSupplier = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TSupplier>> => {
  return axios
    .post(`${SUPPLIER_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TSupplier>>) =>
        response.data
    );
};
const createSupplier = async (
  payload: TSupplierForm
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${SUPPLIER_URL}`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};
export const getSupplierById = async (
  id: string
): Promise<DefaultResponseWithData<TSupplier>> => {
  return axios
    .get(`${SUPPLIER_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TSupplier>>) =>
        response.data
    );
};
const editSupplier = async (
  id: string,
  payload: TSupplierForm
): Promise<DefaultResponse> => {
  return axios
    .put(`${SUPPLIER_URL}/${id}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const deleteSupplier = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${SUPPLIER_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};
//#endregion Supplier API

//#region User API

export const getUserByToken = async (
  headers?: RawAxiosRequestHeaders
): Promise<DefaultPayloadResponse<TProfileResponse>> => {
  let request = headers
    ? axios.get(`${USER_URL}/profile`, {
        headers: headers,
      })
    : axios.get(`${USER_URL}/profile`);

  return request.then(
    (response: AxiosResponse<DefaultPayloadResponse<TProfileResponse>>) => {
      return response.data;
    }
  );
};

const changeProfile = async (
  payload: components["schemas"]["UsersPutRequest"]
): Promise<DefaultResponse> => {
  return axios
    .put(`${USER_URL}/changeprofile`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const changePassword = async (
  payload: components["schemas"]["UsersPutChangePasswordByIdRequest"]
): Promise<DefaultResponse> => {
  return axios
    .put(`${USER_URL}/changepassword`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const getUsers = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TUser>> => {
  return axios
    .post(`${USER_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TUser>>) => response.data
    );
};
const getDrivers = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TUser>> => {
  return axios
    .post(`${USER_URL}/drivers/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TUser>>) => response.data
    );
};

const createUser = async (
  payload: components["schemas"]["UsersPostRequest"]
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${USER_URL}`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};

const editUser = async (
  id: string,
  payload: components["schemas"]["UsersPutRequest"]
): Promise<DefaultResponse> => {
  return axios
    .put(`${USER_URL}/${id}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const getUserById = async (
  id: string
): Promise<DefaultResponseWithData<TUserView>> => {
  return axios
    .get(`${USER_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TUserView>>) =>
        response.data
    );
};

export const getAccountTypes = async (): Promise<
  DefaultPayloadResponse<TRole>
> => {
  return axios
    .get(`${USER_URL}/profile`)
    .then((response: AxiosResponse<DefaultPayloadResponse<TRole>>) => {
      return response.data;
    });
};

export const resetPasswordById = async (
  id: string
): Promise<DefaultPayloadResponse<TUserResetResponse>> => {
  return axios
    .put(`${USER_URL}/resetpassword/${id}`)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TUserResetResponse>>) =>
        response.data
    );
};

export const setDriver = async (
  id: string,
  payload: components["schemas"]["UsersPutDriverRequest"]
): Promise<DefaultResponse> => {
  return axios
    .put(`${USER_URL}/setdriver/${id}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

//#endregion User API

//#region Role API
const getRoles = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TRoleResponse>> => {
  return axios
    .post(`${ROLE_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TRoleResponse>>) =>
        response.data
    );
};

const createRole = async (
  payload: components["schemas"]["CreateSubAccountTypePermissionsPostRequest"]
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${ROLE_URL}`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};

const editRole = async (payload: {
  id: number;
  addPermissions: string[];
  removePermissions: string[];
}): Promise<DefaultResponse> => {
  return axios
    .put(`${ROLE_URL}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};
//#endregion Role API

//#region Manufacturer API
const getManufacturer = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TManufacturer>> => {
  return axios
    .post(`${MANUFACTURER_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TManufacturer>>) =>
        response.data
    );
};
const createManufacturer = async (
  payload: TManufacturerForm
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${MANUFACTURER_URL}`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};
export const getManufacturerById = async (
  id: string
): Promise<DefaultResponseWithData<TManufacturer>> => {
  return axios
    .get(`${MANUFACTURER_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TManufacturer>>) =>
        response.data
    );
};
const editManufacturer = async (
  id: string,
  payload: TManufacturerForm
): Promise<DefaultResponse> => {
  return axios
    .put(`${MANUFACTURER_URL}/${id}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const deleteManufacturer = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${MANUFACTURER_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};
//#endregion Manufacturer API

//#region Country API
const getCountry = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TCountry>> => {
  return axios
    .post(`${COUNTRY_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TCountry>>) => response.data
    );
};
//#endregion Country API

//#region Model API
const createModel = async (
  payload: TModelForm
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${MODEL_URL}`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};

const getModel = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TModel>> => {
  return axios
    .post(`${MODEL_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TModel>>) => response.data
    );
};
export const getModelById = async (
  id: string
): Promise<DefaultResponseWithData<TModel>> => {
  return axios
    .get(`${MODEL_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TModel>>) =>
        response.data
    );
};
const editModel = async (
  id: string,
  payload: TModelForm
): Promise<DefaultResponse> => {
  return axios
    .put(`${MODEL_URL}/${id}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const deleteModel = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${MODEL_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};
//#endregion Model API

//#region Dashboard API
const getSalesOrderMetrics = async (
  payload: TSaleOrderRequest
): Promise<DefaultPayloadResponse<TSaleOrderMetrics>> => {
  return axios
    .post(`${DASHBOARD_URL}/salesordermetrics`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TSaleOrderMetrics>>) =>
        response.data
    );
};
// const getSalesOrderMetrics = async (): Promise<
//   DefaultPayloadResponse<TSalesOrderMetricsResponse>
// > => {
//   return axios
//     .get(`${DASHBOARD_URL}/salesordermetrics`)
//     .then(
//       (
//         response: AxiosResponse<DefaultPayloadResponse<TSalesOrderMetricsResponse>>
//       ) => response.data
//     );
// };
const getSupplierReturnMetrics = async (
  payload: TSaleOrderRequest
): Promise<DefaultPayloadResponse<TSaleOrderMetrics>> => {
  return axios
    .post(`${DASHBOARD_URL}/supplierreturnmetrics`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TSaleOrderMetrics>>) =>
        response.data
    );
};
// const getSupplierReturnMetrics = async (): Promise<
//   DefaultPayloadResponse<TSalesOrderMetricsResponse>
// > => {
//   return axios
//     .get(`${DASHBOARD_URL}/supplierreturnmetrics`)
//     .then(
//       (
//         response: AxiosResponse<DefaultPayloadResponse<TSalesOrderMetricsResponse>>
//       ) => response.data
//     );
// };
const getProductionOrderMetrics = async (
  payload: TSaleOrderRequest
): Promise<DefaultPayloadResponse<TSaleOrderMetrics>> => {
  return axios
    .post(`${DASHBOARD_URL}/productionordermetrics`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TSaleOrderMetrics>>) =>
        response.data
    );
};
// const getProductionOrderMetrics = async (): Promise<
//   DefaultPayloadResponse<TSalesOrderMetricsResponse>
// > => {
//   return axios
//     .get(`${DASHBOARD_URL}/productionordermetrics`)
//     .then(
//       (
//         response: AxiosResponse<DefaultPayloadResponse<TSalesOrderMetricsResponse>>
//       ) => response.data
//     );
// };
const getCustomerReturnMetrics = async (
  payload: TSaleOrderRequest
): Promise<DefaultPayloadResponse<TSaleOrderMetrics>> => {
  return axios
    .post(`${DASHBOARD_URL}/customerreturnmetrics`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TSaleOrderMetrics>>) =>
        response.data
    );
};
// const getCustomerReturnMetrics = async (): Promise<
//   DefaultPayloadResponse<TSalesOrderMetricsResponse>
// > => {
//   return axios
//     .get(`${DASHBOARD_URL}/customerreturnmetrics`)
//     .then(
//       (
//         response: AxiosResponse<DefaultPayloadResponse<TSalesOrderMetricsResponse>>
//       ) => response.data
//     );
// };
const getPurchaseOrderMetrics = async (
  payload: TSaleOrderRequest
): Promise<DefaultPayloadResponse<TSaleOrderMetrics>> => {
  return axios
    .post(`${DASHBOARD_URL}/purchaseordermetrics`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TSaleOrderMetrics>>) =>
        response.data
    );
};
// const getPurchaseOrderMetrics = async (): Promise<
//   DefaultPayloadResponse<TSalesOrderMetricsResponse>
// > => {
//   return axios
//     .get(`${DASHBOARD_URL}/purchaseordermetrics`)
//     .then(
//       (
//         response: AxiosResponse<DefaultPayloadResponse<TSalesOrderMetricsResponse>>
//       ) => response.data
//     );
// };
const getDeliveringMetrics = async (
  payload: TSaleOrderRequest
): Promise<DefaultPayloadResponse<TSaleOrderMetrics>> => {
  return axios
    .post(`${DASHBOARD_URL}/deliveringmetrics`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TSaleOrderMetrics>>) =>
        response.data
    );
};
// const getDeliveringMetrics = async (): Promise<
//   DefaultPayloadResponse<TDeliveringMetricsResponse>
// > => {
//   return axios
//     .get(`${DASHBOARD_URL}/deliveringmetrics`)
//     .then(
//       (
//         response: AxiosResponse<DefaultPayloadResponse<TDeliveringMetricsResponse>>
//       ) => response.data
//     );
// };
const getDashboardData = async (): Promise<
  DefaultPayloadResponse<TDashboardMetrics>
> => {
  return axios
    .get(`${DASHBOARD_URL}/totalassetitemmetrics`)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TDashboardMetrics>>) =>
        response.data
    );
};
const getAssetCreationMetrics = async (
  payload: TSaleOrderRequest
): Promise<DefaultPayloadResponse<TAssetMovementMetrics>> => {
  return axios
    .post(`${DASHBOARD_URL}/transactiongroupcreationmetricsviainterval`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TAssetMovementMetrics>>) =>
        response.data
    );
};
const getAssetMovement = async (): Promise<
  DefaultPayloadResponse<TAssetMovementMetrics>
> => {
  return axios
    .get(`${DASHBOARD_URL}/transactionGroupCreationMetricsViaInterval`)
    .then(
      (
        response: AxiosResponse<DefaultPayloadResponse<TAssetMovementMetrics>>
      ) => response.data
    );
};

const getModelMetrics = async (): Promise<TModelMetrics> => {
  return axios
    .get(`${DASHBOARD_URL}/modelmetrics`)
    .then((response: AxiosResponse<TModelMetrics>) => response.data);
};

const getStockInMetrics = async (): Promise<
  DefaultPayloadResponse<TStockInMetricsResponse>
> => {
  return axios
    .get(`${DASHBOARD_URL}/stockinmetrics`)
    .then(
      (
        response: AxiosResponse<DefaultPayloadResponse<TStockInMetricsResponse>>
      ) => response.data
    );
};
const getStockOutMetrics = async (): Promise<
  DefaultPayloadResponse<TStockOutMetricsResponse>
> => {
  return axios
    .get(`${DASHBOARD_URL}/stockoutmetrics`)
    .then(
      (
        response: AxiosResponse<
          DefaultPayloadResponse<TStockOutMetricsResponse>
        >
      ) => response.data
    );
};
const getAssetOverviewMetrics = async (): Promise<
  DefaultPayloadResponse<TAssetOverViewMetrics>
> => {
  return axios
    .get(`${DASHBOARD_URL}/stockinstockoutdonutmetrics`)
    .then(
      (
        response: AxiosResponse<DefaultPayloadResponse<TAssetOverViewMetrics>>
      ) => response.data
    );
};

const getPermitExpiringSoon = async (): Promise<
  DefaultPayloadResponse<TPermitExpring>
> => {
  return axios
    .get(`${DASHBOARD_URL}/gettotalassetitemsexpiringortoalertmetrics`)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TPermitExpring>>) =>
        response.data
    );
};

//#endregion Dashboard

//#region AssetIssue API
const createAssetIssue = async (
  payload: TAssetIssueRequest
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${ASSETOPERATION_URL}/transactionoperation`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};

const getAssetIssue = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TAssetIssue>> => {
  return axios
    .post(`${ASSETISSUE_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TAssetIssue>>) =>
        response.data
    );
};
export const getAssetIssueById = async (
  id: string
): Promise<DefaultResponseWithData<TAssetIssue>> => {
  return axios
    .get(`${ASSETISSUE_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TAssetIssue>>) =>
        response.data
    );
};
// const editAssetIssue= async (
//   id: string,
//   payload: TAssetIssueForm
// ): Promise<DefaultResponse> => {
//   return axios
//     .put(`${ASSETISSUE_URL}/${id}`, payload)
//     .then((response: AxiosResponse<DefaultResponse>) => response.data);
// };

const deleteAssetIssue = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${ASSETISSUE_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const getIssue = async (
  id: string
): Promise<DefaultResponseWithData<TIssue>> => {
  return Promise.allSettled([
    getTransactionGroupsById(id),
    getTranasctionGroupItems(
      getTableQuery({
        filters: [
          {
            open: false,
            id: 1,
            key: "transactionGroupId",
            title: "TransactionGroupId",
            kind: "built-in",
            compare: "is",
            type: "guid",
            value: id,
          },
        ],
        sorts: [],
        page: 0,
        pageSize: 1000,
        search: "",
      })
    ),
  ]).then(
    (
      responses: [
        PromiseSettledResult<DefaultResponseWithData<TTransactionGroup>>,
        PromiseSettledResult<DefaultSieveResponse<TTransactionGroupItem>>
      ]
    ) => {
      const [transactionGroupResponse, transactionGroupItemResponse] =
        responses;

      if (
        transactionGroupResponse.status === "rejected" ||
        transactionGroupItemResponse.status === "rejected"
      ) {
        return {
          success: false,
          message: "Something went wrong while fetching data.",
        };
      }

      if (
        transactionGroupResponse.value.success === false ||
        transactionGroupItemResponse.value.success === false
      ) {
        return {
          success: false,
          message: "Something went wrong while fetching data.",
        };
      }

      if (!transactionGroupResponse.value.payload) {
        return {
          success: false,
          message: "Something went wrong while fetching data.",
        };
      }

      return {
        success: true,
        message: "Picklist fetched",
        payload: {
          data: {
            ...transactionGroupResponse.value.payload.data,
            items: transactionGroupItemResponse.value.payload,
          },
        },
      };
    }
  );
};
//#endregion AssetIssue API

//#region Transactions API
const getTransactions = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TTransactionList>> => {
  return axios
    .post(`${TRANSACTIONS_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TTransactionList>>) =>
        response.data
    );
};
const getServiceNotes = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TServiceNoteList>> => {
  return axios
    .post(`${TRANSACTIONS_URL}/servicenotes`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TServiceNoteList>>) =>
        response.data
    );
};

//#endregion Transactions API

//#region TransactionGroups API
const createTransactionGroups = async (
  payload: TTransactionGroupRequest
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${TRANSACTIONGROUPS_URL}`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};

const getTransactionGroups = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TTransactionGroup>> => {
  return axios
    .post(`${TRANSACTIONGROUPS_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TTransactionGroup>>) =>
        response.data
    );
};
export const getTransactionGroupsById = async (
  id: string
): Promise<DefaultResponseWithData<TTransactionGroup>> => {
  return axios
    .get(`${TRANSACTIONGROUPS_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TTransactionGroup>>) =>
        response.data
    );
};
const editTransactionGroups = async (
  id: string,
  payload: TTransactionGroupRequest
): Promise<DefaultResponse> => {
  return axios
    .put(`${TRANSACTIONGROUPS_URL}/${id}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const deleteTransactionGroups = async (
  id: string
): Promise<DefaultResponse> => {
  return axios
    .delete(`${TRANSACTIONGROUPS_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

//#endregion TransactionGroup API

//#region TransactionGroupItems API
const getTranasctionGroupItems = async (
  payload: SieveRequestPayload, 
  config: AxiosRequestConfig = {}
): Promise<DefaultSieveResponse<TTransactionGroupItem>> => {
  return axios
    .post(`${TRANSACTION_GROUP_ITEM_URL}/list`, payload, config)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TTransactionGroupItem>>) =>
        response.data
    );
};

const getVesselStatusReport = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TVesselStatusList>> => {
  return axios
    .post(`${REPORT_URL}/vesselstatus`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TVesselStatusList>>) =>
        response.data
    );
};

//#endregion TransactionGroupItems API

//#region Notification API

const getNotifications = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TNotificationList>> => {
  return axios
    .post(`${NOTIFICATION_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TNotificationList>>) =>
        response.data
    );
};

//#endregion Notification API

//#region EmailAlertGroup API

const getEmailAlertGroups = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TEmailAlertGroupList>> => {
  return axios
    .post(`${EMAIL_ALERT_GROUP_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TEmailAlertGroupList>>) =>
        response.data
    );
};

const deleteEmailAlertGroup = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${EMAIL_ALERT_GROUP_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const getEmailAlertGroupById = async (
  id: string
): Promise<DefaultResponseWithData<TEmailAlertGroup>> => {
  return axios
    .get(`${EMAIL_ALERT_GROUP_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TEmailAlertGroup>>) =>
        response.data
    );
};

const editEmailAlertGroup = async (
  id: string,
  payload: TEmailAlertGroupRequest
): Promise<DefaultResponse> => {
  return axios
    .put(`${EMAIL_ALERT_GROUP_URL}/${id}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const createEmailAlertGroup = async (
  payload: TEmailAlertGroupRequest
): Promise<DefaultResponse> => {
  return axios
    .post(`${EMAIL_ALERT_GROUP_URL}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const updateEmailAlertGroupStatus = async (
  id: string,
  payload: { enable: boolean }
): Promise<DefaultResponse> => {
  return axios
    .post(`${EMAIL_ALERT_GROUP_URL}/enable/${id}`, null, { params: payload })
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

//#region TransactionOperations API

const getTransactionOperations = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TTransactionOperation>> => {
  return axios
    .post(`${TRANSACTIONOPERATIONS_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TTransactionOperation>>) =>
        response.data
    );
};
const getAllowedOperationsByIds = async (
  payload: TTransactionOperationRequest
): Promise<DefaultResponseWithData<TTransactionAllowedOperation>> => {
  return axios
    .post(`${TRANSACTIONOPERATIONS_URL}/allowed`, payload)
    .then(
      (
        response: AxiosResponse<
          DefaultResponseWithData<TTransactionAllowedOperation>
        >
      ) => response.data
    );
};

const getAllowedStatesById = async (
  id: string
): Promise<DefaultResponseWithData<TTransactionAllowedState>> => {
  return axios
    .get(`${TRANSACTIONOPERATIONS_URL}/allowedtransactionstates/${id}`)
    .then(
      (
        response: AxiosResponse<
          DefaultResponseWithData<TTransactionAllowedState>
        >
      ) => response.data
    );
};
//#endregion TransactionOperations API

//#region Reports API

const getVesselSummary = async (
  payload: TVesselSummaryPayload
): Promise<DefaultSieveResponse<TVesselSummaryList>> => {
  return axios
    .post(`${REPORT_URL}/vesselsummaryreport`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TVesselSummaryList>>) =>
        response.data
    );
};

const getBoatUsageCsv = async (payload: TBoatUsageRequest) => {
  return axios
    .post(`${REPORT_URL}/boatusage`, payload, { responseType: "blob" })
    .then((blob) => {
      const url = window.URL.createObjectURL(blob.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `boat_usage-${new Date().getTime()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    });
};

const getBoatUsage = async (payload: TBoatUsageRequest): Promise<string> => {
  return axios
    .post(`${REPORT_URL}/boatusage`, payload)
    .then((response: AxiosResponse<string>) => response.data);
};

const getBoatUsageList = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<any>> => {
  let finalPayload = {
    from: "0",
    to: "0",
    boatTypeIds: [""],
  };
  if (payload.filters) {
    const filter = payload.filters.split(",");
    finalPayload.from = filter[0].split("==")[1];
    finalPayload.to = filter[1].split("==")[1];
    finalPayload.boatTypeIds = filter[2].split("==")[1].split("|");
  } else {
    const data: object[] = [];
    return {
      success: true,
      message: "Successfully fetched asset item",
      payload: {
        data,
        page: 1,
        records: 1,
        totalPages: 1,
        totalRecords: 1,
        filteredRecords: 1,
      },
    };
  }

  return axios
    .post(`${REPORT_URL}/boatusage`, finalPayload)
    .then((response: AxiosResponse<string>) => {
      let dataList = response.data.split("\r\n");
      let result: object[] = [];
      let header = dataList[0].split(",");
      for (let i = 1; i < dataList.length - 1; i++) {
        const obj: any = {};
        const currentLine = dataList[i].split(",");
        for (let j = 0; j < header.length; j++) {
          // if(j === 0) {
          //   obj["id"] = currentLine[j].trim();
          // }else{

          // }
          obj[`${j}`] = currentLine[j].trim();
        }
        result.push(obj);
      }

      const { filters = undefined } = payload;
      const pageSize =
        typeof payload.pageSize === "number"
          ? payload.pageSize
          : parseInt(payload?.pageSize ?? "10");
      const page =
        typeof payload.page === "number"
          ? payload.page
          : parseInt(payload?.page ?? "1");

      let first = filters?.split(",");
      first?.splice(0, 3);
      // Filter
      if (first && first != undefined && first.length != 0) {
        first.map((item, index) => {
          let filterKeys: any[] = [];
          let value = item.replace("*", "").split("=");
          let filterValue = value[value.length - 1].toLowerCase().trim();
          if (value[0].includes("|")) {
            filterKeys.push(
              value[0]
                .replace("(", "")
                .replace(")", "")
                .replace("@", "")
                .split("|")
            );
          }
          let dataTable = result;
          let filteredData = dataTable.map((record: any) => {
            let dataFound = 0;
            Object.keys(record).forEach((key: any) => {
              let isTrue = value[0].includes("@")
                ? record[key].toLowerCase().trim().includes(filterValue)
                : record[key].toLowerCase().trim() === filterValue;
              if (filterKeys.length !== 0) {
                filterKeys[0].map((filterKey: any) => {
                  if (
                    key === filterKey &&
                    typeof record[key] === "string" &&
                    isTrue
                  ) {
                    dataFound++;
                  }
                });
              } else {
                if (
                  (key === value[0].replace("@", "") ||
                    "filter" === value[0].trim().replace("@", "")) &&
                  typeof record[key] === "string" &&
                  isTrue
                ) {
                  dataFound++;
                }
              }
            });
            return dataFound > 0 ? record : null;
          });

          result = filteredData.filter((x) => x !== null);
        });
      }

      const { data, totalPages } = paginateTable(result, page, pageSize);

      return {
        success: true,
        message: "Successfully fetched asset item",
        payload: {
          data,
          from: 1 + (page - 1) * pageSize,
          to: page * pageSize,
          page: page,
          records: result.length,
          pageSize: pageSize,
          totalPages: totalPages,
          totalRecords: result.length,
          filteredRecords: result.length ?? 0,
        },
      };
    });
};

const getTimeOfUseCsv = async (payload: TTimeOfUseRequest) => {
  return axios
    .post(`${REPORT_URL}/timeofuse`, payload, { responseType: "blob" })
    .then((blob) => {
      const url = window.URL.createObjectURL(blob.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `time_of_use-${new Date().getTime()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    });
};

const getTimeOfUse = async (payload: TTimeOfUseRequest): Promise<string> => {
  return axios
    .post(`${REPORT_URL}/timeofuse`, payload)
    .then((response: AxiosResponse<string>) => response.data);
};
const getTimeOfUseList = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<any>> => {
  let finalPayload = {
    from: "0",
    to: "0",
  };
  if (payload.filters) {
    const filter = payload.filters.split(",");
    finalPayload.from = filter[0].split("==")[1];
    finalPayload.to = filter[1].split("==")[1];
  }
  return axios
    .post(`${REPORT_URL}/timeofuse`, finalPayload)
    .then((response: AxiosResponse<string>) => {
      let dataList = response.data.split("\r\n");
      let result: object[] = [];
      let header = dataList[0].split(",");
      for (let i = 1; i < dataList.length - 1; i++) {
        const obj: any = {};
        const currentLine = dataList[i].split(",");
        for (let j = 0; j < header.length; j++) {
          // if(j === 0) {
          //   obj["id"] = currentLine[j].trim();
          // }else{

          // }
          obj[`${j}`] = currentLine[j].trim();
        }
        result.push(obj);
      }

      const { filters = undefined } = payload;
      const pageSize =
        typeof payload.pageSize === "number"
          ? payload.pageSize
          : parseInt(payload?.pageSize ?? "10");
      const page =
        typeof payload.page === "number"
          ? payload.page
          : parseInt(payload?.page ?? "1");

      let first = filters?.split(",");
      first?.splice(0, 2);
      // Filter
      if (first && first != undefined && first.length != 0) {
        first.map((item, index) => {
          let filterKeys: any[] = [];
          let value = item.replace("*", "").split("=");
          let filterValue = value[value.length - 1].toLowerCase().trim();
          if (value[0].includes("|")) {
            filterKeys.push(
              value[0]
                .replace("(", "")
                .replace(")", "")
                .replace("@", "")
                .split("|")
            );
          }
          let dataTable = result;
          let filteredData = dataTable.map((record: any) => {
            let dataFound = 0;
            Object.keys(record).forEach((key: any) => {
              let isTrue = value[0].includes("@")
                ? record[key].toLowerCase().trim().includes(filterValue)
                : record[key].toLowerCase().trim() === filterValue;
              if (filterKeys.length !== 0) {
                filterKeys[0].map((filterKey: any) => {
                  if (
                    key === filterKey &&
                    typeof record[key] === "string" &&
                    isTrue
                  ) {
                    dataFound++;
                  }
                });
              } else {
                if (
                  (key === value[0].replace("@", "") ||
                    "filter" === value[0].trim().replace("@", "")) &&
                  typeof record[key] === "string" &&
                  isTrue
                ) {
                  dataFound++;
                }
              }
            });
            return dataFound > 0 ? record : null;
          });

          result = filteredData.filter((x) => x !== null);
        });
      }

      const { data, totalPages } = paginateTable(result, page, pageSize);

      return {
        success: true,
        message: "Successfully fetched asset item",
        payload: {
          data,
          from: 1 + (page - 1) * pageSize,
          to: page * pageSize,
          page: page,
          records: result.length,
          pageSize: pageSize,
          totalPages: totalPages,
          totalRecords: result.length,
          filteredRecords: result.length ?? 0,
        },
      };
    });
};

//#endregion Reports API

//#region StockVerification API
const getStockVerification = async (
  id: string
): Promise<DefaultResponseWithData<TStockVerification>> => {
  return Promise.allSettled([
    getStockTakeById(id),
    getStockTakeLineItem(
      getTableQuery({
        filters: [
          {
            open: false,
            id: 1,
            key: "stockTakeId",
            title: "stockTakeId",
            kind: "built-in",
            compare: "is",
            type: "guid",
            value: id,
          },
        ],
        sorts: [],
        page: 0,
        pageSize: 1000,
        search: "",
      })
    ),
  ]).then(
    (
      responses: [
        PromiseSettledResult<DefaultResponseWithData<TStockTake>>,
        PromiseSettledResult<DefaultSieveResponse<TStockTakeLineItem>>
      ]
    ) => {
      const [stockTakeResponse, stockTakeLineItemResponse] = responses;

      if (
        stockTakeResponse.status === "rejected" ||
        stockTakeLineItemResponse.status === "rejected"
      ) {
        return {
          success: false,
          message: "Something went wrong while fetching data.",
        };
      }

      if (stockTakeResponse.value.success === false) {
        return {
          success: false,
          message: "Something went wrong while fetching data.",
        };
      }

      if (!stockTakeResponse.value.payload) {
        return {
          success: false,
          message: "Something went wrong while fetching data.",
        };
      }

      return {
        success: true,
        message: "Stock verification fetched",
        payload: {
          data: {
            ...stockTakeResponse.value.payload.data,
            items: stockTakeLineItemResponse.value.payload,
          },
        },
      };
    }
  );
};
//#endregion StockVerificaton

//#region StockTake API
const getStockTake = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TStockTake>> => {
  return axios
    .post(`${STOCKTAKES_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TStockTake>>) =>
        response.data
    );
};

const getStockTakeById = async (
  id: string
): Promise<DefaultResponseWithData<TStockTake>> => {
  return axios
    .get(`${STOCKTAKES_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TStockTake>>) =>
        response.data
    );
};

const createStockTake = async (
  payload: TNewStockTake
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${STOCKTAKES_URL}`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};

const checkStockTake = async (
  payload: TCheckStockTake
): Promise<DefaultPayloadResponse<TStockVerificationCheckResponse>> => {
  return axios
    .post(`${STOCKTAKES_URL}/check`, payload)
    .then(
      (
        response: AxiosResponse<
          DefaultPayloadResponse<TStockVerificationCheckResponse>
        >
      ) => response.data
    );
};

const completeStockTake = async (
  payload: TCompleteStockTake
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${STOCKTAKES_URL}/complete`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};
//#endregion StockTake

//#region StockTakeLineItem API
const getStockTakeLineItem = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TStockTakeLineItem>> => {
  // payload.filters = payload.filters? `filter@=none | ${payload.filters}` : "filter@=none";
  return axios
    .post(`${STOCKTAKELINEITEMS_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TStockTakeLineItem>>) =>
        response.data
    );
};
//#endregion StockTakeLineItem

//#region StockTakeEvent API
const getStockTakeEvent = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TStockTakeEvent>> => {
  return axios
    .post(`${STOCKTAKEEVENTS_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TStockTakeEvent>>) =>
        response.data
    );
};
//#endregion StockTakeEvent

//#region Tags API

const getTags = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TTag>> => {
  return axios
    .post(`${TAG_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TTag>>) => response.data
    );
};

const deleteTag = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${TAG_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const changeTag = async (
  payload: TTagChangeRequest
): Promise<DefaultPayloadResponse<PrintTagsResponse>> => {
  return axios
    .post(`${API_URL}/changetag`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<PrintTagsResponse>>) =>
        response.data
    );
};
//#endregion Tags

//#region Associates API
const printTags = async (
  payload: TTagForm
): Promise<DefaultPayloadResponse<PrintTagsResponse>> => {
  return axios
    .post(`${API_URL}/print`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<PrintTagsResponse>>) =>
        response.data
    );
};

const reprintTags = async (
  payload: TTagReprintRequest
): Promise<DefaultPayloadResponse<PrintTagsResponse>> => {
  return axios
    .post(`${API_URL}/reprint`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<PrintTagsResponse>>) =>
        response.data
    );
};

const printAssetItem = async (
  payload: TAssetItemPrintRequest
): Promise<DefaultPayloadResponse<PrintTagsResponse>> => {
  return axios
    .post(`${API_URL}/print/assetitem`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<PrintTagsResponse>>) =>
        response.data
    );
};

// const barcodeCheck = async (
//   payload: TBarcodeCheckRequest
// ): Promise<DefaultPayloadResponse<TBarcodeCheckResponse>> => {
//   return axios
//     .post(`${API_URL}/barcodechecker`, payload)
//     .then(
//       (
//         response: AxiosResponse<DefaultPayloadResponse<TBarcodeCheckResponse>>
//       ) => response.data
//     );
// };

//#endregion Associates

//#region Printer API
const sendToPrinter = (
  request: PrinterPayload
): Promise<PrinterServerResponse> => {
  return axios
    .post(`${PRINTER_URL}`, { ...request })
    .then((response: AxiosResponse<PrinterServerResponse>) => response.data);
};
//#endregion Printer API

//#region BoatType API
// const createBoatType = async (
//   payload: TBoatTypeForm
// ): Promise<DefaultResponseWithData<DefaultResponse>> => {
//   return axios
//     .post(`${BOAT_TYPE_URL}`, payload)
//     .then(
//       (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
//         response.data
//     );
// };

const getBoatType = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TBoatType>> => {
  return axios
    .post(`${BOAT_TYPE_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TBoatType>>) =>
        response.data
    );
};
export const getBoatTypeById = async (
  id: string
): Promise<DefaultResponseWithData<TBoatType>> => {
  return axios
    .get(`${BOAT_TYPE_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TBoatType>>) =>
        response.data
    );
};
// const editBoatType = async (
//   id: string,
//   payload: TBoatTypeForm
// ): Promise<DefaultResponse> => {
//   return axios
//     .put(`${BOAT_TYPE_URL}/${id}`, payload)
//     .then((response: AxiosResponse<DefaultResponse>) => response.data);
// };

const deleteBoatType = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${BOAT_TYPE_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};
//#endregion BoatType API

//#region Logs API
const getLogs = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TLogs>> => {
  return axios
    .post(`${LOGS_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TLogs>>) => response.data
    );
};
//#endregion Logs API

//#region Permissionsets API
const getPermissionSets = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TPermissionSets>> => {
  return axios
    .post(`${PERMISSIONSET_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TPermissionSets>>) =>
        response.data
    );
};
//#endregion Permissionsets API

//#region Gantry API
const getReaderStatus = async (): Promise<
  DefaultPayloadResponse<TReaderStatusPayload>
> => {
  return axios
    .get(`${GANTRY_URL}/readerstatus`)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TReaderStatusPayload>>) =>
        response.data
    );
};

//#endregion Gantry API




//#region Incoming Type API
const getIncomingTypes = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TIncomingType>> => {
  return axios
    .post(`${INCOMING_TYPE_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TIncomingType>>) =>
        response.data
    );
};

// const createIncomingType = async (
//   payload: TCategoryForm
// ): Promise<DefaultResponseWithData<DefaultResponse>> => {
//   return axios
//     .post(`${LOCATION_URL}`, payload)
//     .then(
//       (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
//         response.data
//     );
// };
export const getIncomingTypeById = async (
  id: string
): Promise<DefaultResponseWithData<TIncomingType>> => {
  return axios
    .get(`${INCOMING_TYPE_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TIncomingType>>) =>
        response.data
    );
};
// const editIncomingType = async (
//   id: string,
//   payload: TCategoryForm
// ): Promise<DefaultResponse> => {
//   return axios
//     .put(`${LOCATION_URL}/${id}`, payload)
//     .then((response: AxiosResponse<DefaultResponse>) => response.data);
// };
const deleteIncomingType = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${INCOMING_TYPE_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};


//#region Stock Item API
const getStockItems = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TStockItem>> => {
  return axios
    .post(`${STOCK_ITEM_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TStockItem>>) =>
        response.data
    );
};

// const createIncomingType = async (
//   payload: TCategoryForm
// ): Promise<DefaultResponseWithData<DefaultResponse>> => {
//   return axios
//     .post(`${LOCATION_URL}`, payload)
//     .then(
//       (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
//         response.data
//     );
// };
export const getStockItemById = async (
  id: string
): Promise<DefaultResponseWithData<TStockItem>> => {
  return axios
    .get(`${STOCK_ITEM_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TStockItem>>) =>
        response.data
    );
};
// const editIncomingType = async (
//   id: string,
//   payload: TCategoryForm
// ): Promise<DefaultResponse> => {
//   return axios
//     .put(`${LOCATION_URL}/${id}`, payload)
//     .then((response: AxiosResponse<DefaultResponse>) => response.data);
// };
const deleteStockItem = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${STOCK_ITEM_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

//#endregion Stocks API


//#region Stocks API
const getStocks = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TStock>> => {
  return axios
    .post(`${STOCKS_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TStock>>) =>
        response.data
    );
};

// const createIncomingType = async (
//   payload: TCategoryForm
// ): Promise<DefaultResponseWithData<DefaultResponse>> => {
//   return axios
//     .post(`${LOCATION_URL}`, payload)
//     .then(
//       (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
//         response.data
//     );
// };
export const getStockById = async (
  id: string
): Promise<DefaultResponseWithData<TStock>> => {
  return axios
    .get(`${STOCKS_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TStock>>) =>
        response.data
    );
};
// const editIncomingType = async (
//   id: string,
//   payload: TCategoryForm
// ): Promise<DefaultResponse> => {
//   return axios
//     .put(`${LOCATION_URL}/${id}`, payload)
//     .then((response: AxiosResponse<DefaultResponse>) => response.data);
// };
const deleteStock = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${STOCKS_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

//#endregion Stocks API
const getTransactionGroupById = async (
  id: string,
  config: AxiosRequestConfig = {}
): Promise<DefaultResponseWithData<TTransactionGroup>> => {
  return axios
    .get(`${TRANSACTION_GROUP_URL}/${id}`, config)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TTransactionGroup>>) =>
        response.data
    );
};
// Picklist
const getPicklist = async (
  id: string,
  config: AxiosRequestConfig = {}
): Promise<DefaultResponseWithData<TPicklist>> => {
  return Promise.allSettled([
    getTransactionGroupById(id, config),
    getTranasctionGroupItems(
      getTableQuery({
        filters: [
          {
            open: false,
            id: 1,
            key: "transactionGroupId",
            title: "TransactionGroupId",
            kind: "built-in",
            compare: "is",
            type: "guid",
            value: id,
          },
        ],
        sorts: [],
        page: 0,
        pageSize: 1000,
        search: "",
      }), config
    ),
  ]).then(
    (
      responses: [
        PromiseSettledResult<DefaultResponseWithData<TTransactionGroup>>,
        PromiseSettledResult<DefaultSieveResponse<TTransactionGroupItem>>
      ]
    ) => {
      const [transactionGroupResponse, transactionGroupItemResponse] =
        responses;

      if (
        transactionGroupResponse.status === "rejected" ||
        transactionGroupItemResponse.status === "rejected"
      ) {
        return {
          success: false,
          message: "Something went wrong while fetching data.",
        };
      }

      if (
        transactionGroupResponse.value.success === false ||
        transactionGroupItemResponse.value.success === false
      ) {
        return {
          success: false,
          message: "Something went wrong while fetching data.",
        };
      }

      if (!transactionGroupResponse.value.payload) {
        return {
          success: false,
          message: "Something went wrong while fetching data.",
        };
      }

      return {
        success: true,
        message: "Picklist fetched",
        payload: {
          data: {
            ...transactionGroupResponse.value.payload.data,
            items: transactionGroupItemResponse.value.payload,
          },
        },
      };
    }
  );
};

//#region Asset Item Location API
const getAssetItemLocation = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TAssetItemLocation>> => {
  return axios
    .post(`${ASSET_ITEM_LOCATION_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TAssetItemLocation>>) =>
        response.data
    );
};

const createAssetItemLocation = async (
  payload: TAssetItemForm
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${ASSET_ITEM_LOCATION_URL}`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};


export const getAssetItemLocationById = async (
  id: string
): Promise<DefaultResponseWithData<TAssetItemLocation>> => {
  return axios
    .get(`${ASSET_ITEM_LOCATION_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TAssetItemLocation>>) =>
        response.data
    );
};
const editAssetItemLocation = async (
  id: string,
  payload: TAssetItemForm
): Promise<DefaultResponse> => {
  return axios
    .put(`${ASSET_ITEM_LOCATION_URL}/${id}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const deleteAssetItemLocation = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${ASSET_ITEM_LOCATION_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};
//#endregion Asset Item Location API

//#region Asset Type Location API
const getAssetTypeLocation = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TAssetTypeLocation>> => {
  return axios
    .post(`${ASSET_TYPE_LOCATION_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TAssetTypeLocation>>) =>
        response.data
    );
};
const createAssetTypeLocation = async (
  payload: TAssetTypeForm
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${ASSET_TYPE_LOCATION_URL}`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};
export const getAssetTypeLocationById = async (
  id: string
): Promise<DefaultResponseWithData<TAssetTypeLocation>> => {
  return axios
    .get(`${ASSET_TYPE_LOCATION_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TAssetTypeLocation>>) =>
        response.data
    );
};
const editAssetTypeLocation = async (
  id: string,
  payload: TAssetTypeForm
): Promise<DefaultResponse> => {
  return axios
    .put(`${ASSET_TYPE_LOCATION_URL}/${id}`, payload)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};

const deleteAssetTypeLocation = async (id: string): Promise<DefaultResponse> => {
  return axios
    .delete(`${ASSET_TYPE_LOCATION_URL}/${id}`)
    .then((response: AxiosResponse<DefaultResponse>) => response.data);
};
//#endregion Asset Type Location API

//#region Stock Adjustment API
const stockAdjusment = async (
  payload: TStockAdjustmentRequest
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${ASSETOPERATION_URL}/stockadjustment`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};
const removeAssetItemFromBin = async (
  payload: TRemoveAssetItemFromBinRequest
): Promise<DefaultResponse> => {
  return axios
    .post(`${ASSETOPERATION_URL}/removeAssetItemFromBin`, payload)
    .then(
      (response: AxiosResponse<DefaultResponse>) =>
        response.data
    );
};
//#endregion Stock Adjustment API

//#region Allocation API
const allocateAssetType = async (
  payload: TAllocateDeallocateRequest
): Promise<DefaultPayloadResponse<TAllocationReponse>> => {
  return axios
    .post(`${ALLOCATION_URL}/allocateassettypetolocation`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TAllocationReponse>>) =>
        response.data
    );
};
const deallocateAssetType = async (
  payload: TAllocateDeallocateRequest
): Promise<DefaultPayloadResponse<TAllocationReponse>> => {
  return axios
    .post(`${ALLOCATION_URL}/deallocateassettypefromlocation`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TAllocationReponse>>) =>
        response.data
    );
};
//#endregion Allocation API

//#region Unit of Measurement API
export const getMeasurementUnitById = async (
  id: string
): Promise<DefaultResponseWithData<TMeasurementUnit>> => {
  return axios
    .get(`${MEASUREMENT_UNIT_URL}/${id}`)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<TMeasurementUnit>>) =>
        response.data
    );
};

const getMeasurementUnits = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TMeasurementUnit>> => {
  return axios
    .post(`${MEASUREMENT_UNIT_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TMeasurementUnit>>) =>
        response.data
    );
};
//#endregion Unit of Measurement API

//#region Assign Driver API
const assignDriver = async (
  payload: TAssignDriverRequest
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${ASSETOPERATION_URL}/assigndriver`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};
//#endregion Assign Driver  API

//#region Close Transaction Group API
const closeTransactionGroup = async (
  payload: TCloseTransactionGroupRequest
): Promise<DefaultResponseWithData<DefaultResponse>> => {
  return axios
    .post(`${ASSETOPERATION_URL}/close`, payload)
    .then(
      (response: AxiosResponse<DefaultResponseWithData<DefaultResponse>>) =>
        response.data
    );
};
//#endregion Close Transaction Group API

//#region Close Transaction Group Statuses API
const getTransactionGroupStatus = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TTransactionGroupStatus>> => {
  return axios
    .post(`${TRANSACTION_GROUP_STATUS_URL}/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TTransactionGroupStatus>>) =>
        response.data
    );
};
//#endregion Close Transaction Group API

//#region Modified Item
const getModifiedItems = async (
  payload: SieveRequestPayload
): Promise<DefaultSieveResponse<TModifiedItems>> => {
  return axios
    .post(`${ASSETOPERATION_URL}/outgoing/salesorder/list`, payload)
    .then(
      (response: AxiosResponse<DefaultSieveResponse<TModifiedItems>>) =>
        response.data
    );
};
const getItemChangeLog = async (
  payload: TRequestItemLogs
): Promise<DefaultPayloadResponse<TItemLogs[]>> => {
  return axios
    .post(`${ASSETOPERATION_URL}/outgoing/salesorder/changelog`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TItemLogs[]>>) =>
        response.data
    );
};
const getComparison = async (
  payload: TCompareRequest
): Promise<DefaultPayloadResponse<TCompareResult>> => {
  return axios
    .post(`${ASSETOPERATION_URL}/outgoing/salesorder/compare`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TCompareResult>>) =>
        response.data
    );
};
const getVersions = async (
  payload: TVersionRequest
): Promise<DefaultPayloadResponse<TVersionResponse[]>> => {
  return axios
    .post(`${ASSETOPERATION_URL}/outgoing/salesorder/revisions`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TVersionResponse[]>>) =>
        response.data
    );
};
const approveChanges = async (
  payload: TApproveRequest
): Promise<DefaultPayloadResponse<TApproveResult>> => {
  return axios
    .post(`${ASSETOPERATION_URL}/outgoing/salesorder/lineitem/approve`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<TApproveResult>>) =>
        response.data
    );
};

const pickedSalesOrderItems = async (
  payload: TPickRequest
): Promise<DefaultPayloadResponse<DefaultResponse>> => {
  return axios
    .post(`${ASSETOPERATION_URL}/outgoing/salesorder/pick`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<DefaultResponse>>) =>
        response.data
    );
};
const pickedProductionOrderItems = async (
  payload: TPickRequest
): Promise<DefaultPayloadResponse<DefaultResponse>> => {
  return axios
    .post(`${ASSETOPERATION_URL}/outgoing/productionorder/pick`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<DefaultResponse>>) =>
        response.data
    );
};
const pickedPurchaseReturnItems = async (
  payload: TPickRequest
): Promise<DefaultPayloadResponse<DefaultResponse>> => {
  return axios
    .post(`${ASSETOPERATION_URL}/outgoing/purchasereturn/pick`, payload)
    .then(
      (response: AxiosResponse<DefaultPayloadResponse<DefaultResponse>>) =>
        response.data
    );
};
//#endregion Modified Item
export default {
  getCountry,
  getAuthToken,
  getUserByToken,
  getTransactionTypeById,
  changeProfile,
  changePassword,
  getUsers,
  createUser,
  getAccountTypes,
  getRoles,
  createRole,
  editRole,
  editUser,
  getUserById,
  createLocation,
  getLocations,
  getLocationById,
  editLocation,
  deleteLocation,
  createCategory,
  getCategory,
  getCategoryById,
  editCategory,
  deleteCategory,
  getAssetType,
  createAssetType,
  editAssetType,
  getAssetTypeById,
  deleteAssetType,
  getAssetItem,
  createAssetItem,
  editAssetItem,
  getAssetItemById,
  deleteAssetItem,
  createMultipleAssetItem,
  getSupplier,
  createSupplier,
  editSupplier,
  getSupplierById,
  deleteSupplier,
  getManufacturer,
  createManufacturer,
  editManufacturer,
  getManufacturerById,
  deleteManufacturer,
  getModel,
  createModel,
  editModel,
  getModelById,
  deleteModel,
  getModelMetrics,
  getStockInMetrics,
  getStockOutMetrics,
  getAssetOverviewMetrics,
  getAssetIssue,
  deleteAssetIssue,
  createAssetIssue,
  getAssetIssueById,
  getAllowedOperationsByIds,
  getTransactionGroups,
  createTransactionGroups,
  editTransactionGroups,
  getTransactionGroupsById,
  deleteTransactionGroups,
  getAllowedStatesById,
  getTransactionOperations,
  getTranasctionGroupItems,
  getDashboardData,
  getBoatUsageCsv,
  getTimeOfUseCsv,
  getStockVerification,
  getStockTake,
  checkStockTake,
  createStockTake,
  completeStockTake,
  getStockTakeLineItem,
  getStockTakeEvent,
  getPermitExpiringSoon,
  getBoatUsageList,
  getBoatUsage,
  getTimeOfUse,
  getTimeOfUseList,
  printTags,
  reprintTags,
  sendToPrinter,
  getTags,
  deleteTag,
  getVesselStatusReport,
  getTransactionTypes,
  getAssetMovement,
  // createBoatType,
  getBoatType,
  getBoatTypeById,
  // editBoatType,
  deleteBoatType,
  getIssue,
  getTransactions,
  resetPasswordById,
  getServiceNotes,
  getLogs,
  getPermissionSets,
  changeTag,
  getEmailAlertGroups,
  deleteEmailAlertGroup,
  createEmailAlertGroup,
  getEmailAlertGroupById,
  editEmailAlertGroup,
  updateEmailAlertGroupStatus,
  getNotifications,
  getReaderStatus,
  getVesselSummary,



  getIncomingTypeById,
  getIncomingTypes,
  deleteIncomingType,
  getStockById,
  getStocks,
  deleteStock,
  getStockItemById,
  getStockItems,
  deleteStockItem,
  getPicklist,
  getAssetItemLocation,
  getAssetItemLocationById,
  editAssetItemLocation,
  deleteAssetItemLocation,
  getAssetTypeLocation,
  getAssetTypeLocationById,
  stockAdjusment,
  getMeasurementUnitById,
  getMeasurementUnits,
  allocateAssetType,
  deallocateAssetType,
  assignDriver,
  closeTransactionGroup,
  getSalesOrderMetrics,
  getSupplierReturnMetrics,
  getProductionOrderMetrics,
  getCustomerReturnMetrics,
  getPurchaseOrderMetrics,
  getDeliveringMetrics,
  removeAssetItemFromBin,
  updateBatchNumber,
  getTransactionGroupById,
  getTransactionGroupStatus,
  getAssetCreationMetrics,
  getModifiedItems,
  getItemChangeLog,
  getComparison,
  approveChanges,
  pickedProductionOrderItems,
  pickedPurchaseReturnItems,
  pickedSalesOrderItems,
  getVersions,
  getWarehouses,
  getDrivers,
  setDriver,
};
